import React, { useState } from 'react';
import Seo from './common/Seo';
import { FaStar, FaQuoteLeft, FaWhatsapp, FaChevronLeft, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { TESTIMONIALS } from '../data';
import { COMPANY_INFO } from '../constants';

// Enlaza cada testimonio con la landing page del servicio que menciona
const SERVICE_PAGE_BY_LABEL = {
  'Cuidados Postoperatorios': { path: '/atencion-postoperatoria', label: 'Atención Postoperatoria' },
  'Enfermería 24 Horas': { path: '/enfermeria-24-horas', label: 'Enfermería 24 Horas' },
  'Cuidados Geriátricos': { path: '/cuidados-geriatricos', label: 'Cuidados Geriátricos' },
  'Cuidadoras': { path: '/services', label: 'nuestros servicios de cuidadoras' },
  'Auxiliar de Enfermería': { path: '/services', label: 'nuestros servicios de enfermería' }
};

const TestimonialCard = ({ testimonial, featured = false }) => {
  const relatedService = testimonial.service ? SERVICE_PAGE_BY_LABEL[testimonial.service] : null;

  return (
  <article className={`bg-white rounded-2xl shadow-lg p-8 ${featured ? 'border-2 border-blue-500' : ''}`}>
    <div className="flex items-center gap-1 mb-4">
      {[...Array(5)].map((_, i) => (
        <FaStar 
          key={i} 
          className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`} 
        />
      ))}
    </div>
    
    <div className="relative">
      <FaQuoteLeft className="absolute -top-2 -left-2 text-4xl text-blue-100" />
      <p className="text-gray-700 text-lg italic leading-relaxed pl-6">
        "{testimonial.content}"
      </p>
    </div>
    
    <div className="mt-6 flex items-center gap-4">
      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
        {testimonial.name.charAt(0)}
      </div>
      <div>
        <h3 className="font-bold text-gray-900">{testimonial.name}</h3>
        <p className="text-gray-500">{testimonial.role}</p>
        {testimonial.location && (
          <p className="text-sm text-gray-400">{testimonial.location}</p>
        )}
      </div>
    </div>
    
    {testimonial.service && (
      <div className="mt-4 pt-4 border-t flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
          Servicio: {testimonial.service}
        </span>
        {relatedService && (
          <Link
            to={relatedService.path}
            className="text-sm text-blue-600 font-semibold hover:underline flex items-center gap-1"
          >
            Ver {relatedService.label} <FaArrowRight className="text-xs" />
          </Link>
        )}
      </div>
    )}
  </article>
  );
};

const TestimoniosPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Extender testimonios con más datos
  const allTestimonials = [
    ...TESTIMONIALS,
    {
      id: 4,
      name: "Roberto Hernández",
      role: "Hijo de paciente",
      content: "Después de la cirugía de cadera de mi madre, el equipo de Geriatría Serena fue fundamental en su recuperación. Las enfermeras son muy profesionales y siempre están atentas a cualquier necesidad.",
      rating: 5,
      location: "Zapopan, Jalisco",
      service: "Cuidados Postoperatorios"
    },
    {
      id: 5,
      name: "Patricia López",
      role: "Familiar de paciente",
      content: "Excelente servicio de enfermería nocturna. Por fin pudimos descansar tranquilos sabiendo que mi papá estaba en buenas manos durante la noche.",
      rating: 5,
      location: "Guadalajara, Jalisco",
      service: "Enfermería 24 Horas"
    },
    {
      id: 6,
      name: "Fernando García",
      role: "Esposo de paciente",
      content: "Contratamos el servicio para mi esposa con Alzheimer. La cuidadora no solo es profesional, también es muy paciente y cariñosa. Ha sido un gran alivio para toda la familia.",
      rating: 5,
      location: "Tlaquepaque, Jalisco",
      service: "Cuidados Geriátricos"
    },
    {
      id: 7,
      name: "Claudia Ramírez",
      role: "Hija de paciente",
      content: "El personal es muy puntual y responsable. Siempre llegan a tiempo y nos mantienen informados sobre el estado de salud de mi mamá. Totalmente recomendados.",
      rating: 5,
      location: "Zapopan, Jalisco",
      service: "Cuidadoras"
    },
    {
      id: 8,
      name: "Miguel Sánchez",
      role: "Familiar de paciente",
      content: "Después de buscar varias opciones, encontramos en Geriatría Serena el servicio ideal. La relación calidad-precio es excelente y el trato humano incomparable.",
      rating: 5,
      location: "Guadalajara, Jalisco",
      service: "Auxiliar de Enfermería"
    }
  ];

  // Testimonios destacados para el carrusel
  const featuredTestimonials = allTestimonials.slice(0, 3);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredTestimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredTestimonials.length) % featuredTestimonials.length);
  };

  // Estadísticas
  const stats = [
    { number: "500+", label: "Familias Atendidas" },
    { number: "98%", label: "Satisfacción" },
    { number: "5.0", label: "Calificación Promedio" },
    { number: "24/7", label: "Disponibilidad" }
  ];

  return (
    <>
      <Seo
        title={`Testimonios de Clientes | ${COMPANY_INFO.name}`}
        description="Lee las experiencias de familias que han confiado en nuestros servicios de enfermería a domicilio en Guadalajara. Testimonios reales de clientes satisfechos."
        canonical="https://geriatriaserena.com/testimonios"
      />

      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Lo Que Dicen Nuestros Clientes
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              La satisfacción de las familias que confían en nosotros es nuestra mayor recompensa
            </p>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Testimonial Carousel */}
        <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Testimonios Destacados
            </h2>
            
            <div className="relative">
              <TestimonialCard 
                testimonial={featuredTestimonials[currentSlide]} 
                featured 
              />
              
              {/* Navigation */}
              <div className="flex justify-center gap-4 mt-8">
                <button
                  onClick={prevSlide}
                  className="p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                  aria-label="Anterior"
                >
                  <FaChevronLeft className="text-blue-600" />
                </button>
                
                <div className="flex items-center gap-2">
                  {featuredTestimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        currentSlide === index ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                      aria-label={`Ir a testimonio ${index + 1}`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  className="p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                  aria-label="Siguiente"
                >
                  <FaChevronRight className="text-blue-600" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* All Testimonials Grid */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
              Más Testimonios
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              Conoce las experiencias de otras familias que han confiado en nuestros servicios
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {allTestimonials.slice(3).map(testimonial => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              ¿Listo para Experimentar Nuestro Servicio?
            </h2>
            <p className="text-xl text-blue-100 mb-10">
              Únete a las cientos de familias que ya confían en Geriatría Serena
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`https://wa.me/${COMPANY_INFO.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3"
              >
                <FaWhatsapp className="text-2xl" />
                Contactar por WhatsApp
              </a>
              <Link
                to="/contact"
                className="bg-white text-blue-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all"
              >
                Solicitar Información
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default TestimoniosPage;
